import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, ClipboardList, Package, CalendarDays, UserCog, FileText,
  Wallet, Newspaper, Images, Star, HelpCircle, Megaphone, Users2, ScrollText, Settings,
  Menu, Search, Bell, LogOut, X, Landmark, Handshake,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/shared/NotificationBell";

const MENU = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/admin/jamaah", label: "Jamaah", icon: Users },
  { to: "/admin/pendaftaran", label: "Pendaftaran", icon: ClipboardList },
  { to: "/admin/program", label: "Program", icon: Package },
  { to: "/admin/manasik", label: "Jadwal Manasik", icon: CalendarDays },
  { to: "/admin/pembimbing", label: "Pembimbing", icon: UserCog },
  { to: "/admin/dokumen", label: "Dokumen", icon: FileText },
  { to: "/admin/pembayaran", label: "Pembayaran", icon: Wallet },
  { to: "/admin/rekening", label: "Rekening Bank", icon: Landmark, roles: ["super_admin", "admin"] },
  { to: "/admin/affiliate", label: "Affiliate / Agen", icon: Handshake },
  { to: "/admin/artikel", label: "Artikel", icon: Newspaper },
  { to: "/admin/galeri", label: "Galeri", icon: Images },
  { to: "/admin/testimoni", label: "Testimoni", icon: Star },
  { to: "/admin/faq", label: "FAQ", icon: HelpCircle },
  { to: "/admin/pengumuman", label: "Pengumuman", icon: Megaphone },
  { to: "/admin/pengguna", label: "Pengguna", icon: Users2, roles: ["super_admin", "admin"] },
  { to: "/admin/audit-log", label: "Audit Log", icon: ScrollText, roles: ["super_admin", "admin"] },
  { to: "/admin/pengaturan", label: "Pengaturan", icon: Settings, roles: ["super_admin", "admin"] },
];

function SidebarContent({ role, onNavigate }) {
  return (
    <nav className="flex flex-col gap-1 p-4" data-testid="admin-sidebar-nav">
      {MENU.filter((m) => !m.roles || m.roles.includes(role)).map((m) => (
        <NavLink
          key={m.to}
          to={m.to}
          end={m.exact}
          onClick={onNavigate}
          data-testid={`admin-sidebar-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-[#0F6B4F] text-white" : "text-[#1F2937] hover:bg-[#0F6B4F]/8"
            }`
          }
        >
          <m.icon className="h-4 w-4 shrink-0" /> {m.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="flex min-h-screen bg-[#F5F9F7]" data-testid="admin-layout">
      <aside className="hidden w-64 shrink-0 border-r border-slate-100 bg-white lg:block">
        <div className="flex h-16 items-center gap-2 border-b border-slate-100 px-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#0F6B4F] to-[#1677B8] text-white font-heading font-bold">K</div>
          <span className="font-heading text-sm font-bold leading-tight text-[#1F2937]">KBIHU Muhammadiyah Surabaya</span>
        </div>
        <SidebarContent role={user?.role} />
      </aside>

      <div className="flex flex-1 flex-col">
        <header className="flex h-16 items-center justify-between gap-4 border-b border-slate-100 bg-white px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden" data-testid="admin-mobile-menu-toggle" aria-label="Menu">
                  <Menu className="h-5 w-5 text-[#1F2937]" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 bg-white p-0">
                <div className="flex h-16 items-center justify-between border-b border-slate-100 px-5">
                  <span className="font-heading text-sm font-bold leading-tight text-[#1F2937]">KBIHU Muhammadiyah Surabaya</span>
                  <button onClick={() => setMobileOpen(false)} data-testid="admin-mobile-menu-close"><X className="h-5 w-5" /></button>
                </div>
                <SidebarContent role={user?.role} onNavigate={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>
            <div className="relative hidden sm:block">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#64748B]" />
              <input data-testid="admin-search-input" placeholder="Cari..." className="h-10 w-64 rounded-full border border-slate-200 pl-9 pr-4 text-sm outline-none focus:border-[#0F6B4F]" />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <NotificationBell />
            <div className="flex items-center gap-2 border-l border-slate-100 pl-3">
              <div className="hidden text-right sm:block">
                <p className="text-sm font-medium text-[#1F2937]" data-testid="admin-user-name">{user?.name}</p>
                <p className="text-xs capitalize text-[#64748B]">{user?.role?.replace("_", " ")}</p>
              </div>
              <Button variant="ghost" size="icon" data-testid="admin-logout-button" onClick={() => logout().then(() => navigate("/"))} className="text-[#64748B] hover:text-red-600">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-x-hidden p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
